/* eslint-disable @typescript-eslint/no-explicit-any */
import isNil from 'lodash/isNil'
import * as React from 'react'

const DropdownMenuContext = React.createContext<any>(null)

export const DropdownMenuContextProvider = DropdownMenuContext.Provider

export const DropdownMenuContextConsumer = DropdownMenuContext.Consumer

export function useDropdownMenuContext(menuRef: any) {
    const [open, setOpen] = React.useState(false)

    const [items, setItems] = React.useState<any[]>([])
    const [activeItemIndex, setActiveItemIndex] = React.useState<number | null>(
        null
    )
    const previousActiveElementRef = React.useRef<any>(null)

    const registerItem = React.useCallback((element: any, props: any) => {
        setItems((items) => [...items, { element, props }])
    }, [])

    const unregisterItem = React.useCallback((id: any) => {
        setItems((items) => items.filter((item) => item.element.id !== id))
    }, [])

    const focusSelf = React.useCallback(() => {
        requestAnimationFrame(() => {
            if (document.activeElement !== menuRef.current) {
                previousActiveElementRef.current = document.activeElement
                menuRef.current?.focus()
            }
        })
    }, [menuRef])

    const focusItem = React.useCallback(
        (item: any) => {
            const itemIndex = items.indexOf(item)
            if (itemIndex !== -1) {
                setActiveItemIndex(itemIndex)
                focusSelf()
            }
        },
        [items, focusSelf]
    )

    const lookupNextActiveItemIndex = React.useCallback(
        (start: any, direction: any) => {
            for (let i = start; i > -1 && i < items.length; i += direction) {
                if (!items[i].props?.disabled) {
                    return i
                }
            }
            return null
        },
        [items]
    )

    const focusItemAt = React.useCallback(
        (index: number) => {
            if (isNil(index)) {
                setActiveItemIndex(null)
                focusSelf()
            } else {
                let activeItemIndex
                if (index === 0) {
                    activeItemIndex = lookupNextActiveItemIndex(0, 1)
                } else if (index === -1) {
                    activeItemIndex = lookupNextActiveItemIndex(
                        items.length - 1,
                        -1
                    )
                }

                if (!isNil(activeItemIndex)) {
                    focusItem(items[activeItemIndex])
                }
            }
        },
        [items, focusItem, focusSelf, lookupNextActiveItemIndex]
    )

    const openMenu = React.useCallback(() => {
        setOpen(true)
        focusSelf()
    }, [focusSelf])

    const closeMenu = React.useCallback(() => {
        setOpen(false)
        setActiveItemIndex(null)
        requestAnimationFrame(() => {
            previousActiveElementRef.current?.focus()
        })
    }, [])

    return {
        open,
        items,
        activeItemIndex,
        registerItem,
        unregisterItem,
        focusItemAt,
        openMenu,
        closeMenu,
    }
}

export default DropdownMenuContext
